/**
 * The body editor.
 *
 * TipTap (ProseMirror) with its schema cut down to what the site's stylesheet
 * was written for: h2/h3, paragraphs, bold, italic, links, lists, quotes,
 * tables with a caption, images with an alignment, and the "Key Takeaways"
 * callout the existing Articles carry.
 *
 * ## Why the schema is cut down rather than styled down
 *
 * A toolbar without an H1 button still lets somebody paste an H1. ProseMirror
 * only keeps what its schema allows, so anything pasted from Word arrives as
 * the elements below or as plain text — which is the paste-cleaning the brief
 * asks for, done by construction rather than by a list of things to strip.
 * `admin/shared/markup.js` then sanitises the output on the way out and again
 * on the server, because two of the three are defence and only the last is the
 * rule.
 *
 * ## Images
 *
 * An image in the editor is a plain `<img>` carrying `data-align`. It becomes
 * Webflow's `<figure class="w-richtext-figure-type-image …">` on save and comes
 * back as an `<img>` on load (`toContentHtml` / `toEditorHtml`). Doing it at
 * the boundary rather than inside the editor keeps the editor's own document
 * simple and keeps the one shape that matters — what gets stored — in a module
 * with tests over the real 72 Article bodies.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { TableKit } from "@tiptap/extension-table";
import { Node, mergeAttributes } from "@tiptap/core";

import { calloutHtml, readingStats, toContentHtml, toEditorHtml } from "../../shared/markup.js";
import { ImageField, Message } from "../components/bits.jsx";

/**
 * The "Key Takeaways" box, as one indivisible node.
 *
 * Editable as a block rather than as free markup, because its shape is load-
 * bearing: the site styles `.article-summary`, `.tldr` and `.key-takeaways` by
 * those exact class names, and a box assembled by hand would be one typo away
 * from rendering as three stray paragraphs.
 */
const KeyTakeaways = Node.create({
  name: "keyTakeaways",
  group: "block",
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      summary: { default: "" },
      points: {
        default: [],
        parseHTML: (element) => [...element.querySelectorAll("li")].map((li) => li.innerHTML.trim()),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div.article-summary",
        getAttrs: (element) => ({
          summary: element.querySelector(".tldr")?.textContent?.trim() || "",
          points: [...element.querySelectorAll(".key-takeaways li")].map((li) => li.innerHTML.trim()),
        }),
      },
    ];
  },

  renderHTML({ HTMLAttributes, node }) {
    // What the editor *shows*. What gets stored is calloutHtml(), applied in
    // `toStored` below — the editor's own serialisation would not reproduce the
    // inline style the existing boxes carry.
    return [
      "div",
      mergeAttributes(HTMLAttributes, { class: "article-summary", "data-callout": "true" }),
      ["p", {}, ["strong", {}, "Key Takeaways"]],
      ["p", { class: "tldr" }, node.attrs.summary || ""],
      ["ul", { class: "key-takeaways" }, ...(node.attrs.points || []).map((p) => ["li", {}, p])],
    ];
  },
});

/** The editor's HTML -> what is stored, with the callout put back verbatim. */
function toStored(html) {
  const withCallouts = html.replace(
    /<div[^>]*class="article-summary"[^>]*data-callout="true"[^>]*>([\s\S]*?)<\/div>/gi,
    (whole, inner) => {
      const summary = /<p class="tldr">([\s\S]*?)<\/p>/i.exec(inner)?.[1]?.trim() || "";
      const points = [...inner.matchAll(/<li>([\s\S]*?)<\/li>/gi)].map((m) => m[1].trim());
      void whole;
      return calloutHtml({ summary, points });
    }
  );
  return toContentHtml(withCallouts);
}

export default function RichText({ value, onChange, allowJsonLd = false }) {
  const [notes, setNotes] = useState([]);
  const [pickImage, setPickImage] = useState(false);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        // The page already has an <h1>: its title. A second one competes with it
        // in search results, so the editor cannot make one.
        heading: { levels: [2, 3, 4] },
        codeBlock: false,
        horizontalRule: false,
        link: { openOnClick: false, autolink: false, HTMLAttributes: { rel: null, target: null } },
      }),
      Image.configure({ inline: false, allowBase64: false }),
      TableKit.configure({ table: { resizable: false } }),
      KeyTakeaways,
    ],
    []
  );

  const editor = useEditor({
    extensions,
    content: toEditorHtml(value || ""),
    editorProps: { attributes: { class: "body-editor", "aria-label": "Article body" } },
    onUpdate: ({ editor: e }) => {
      const { html, notes: found } = toStored(e.getHTML());
      setNotes(found);
      onChange(html);
    },
  });

  // Loading a different item has to replace the document; without this the
  // editor keeps the one it was created with and the second Article anyone
  // opens shows the first one's words.
  useEffect(() => {
    if (!editor) return;
    const next = toEditorHtml(value || "");
    // `emitUpdate: false` matters: without it, loading a body fires onUpdate
    // and the panel treats the load as an edit — every item you merely opened
    // would come back marked as changed.
    if (toStored(editor.getHTML()).html !== (value || "")) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, value]);

  const addLink = useCallback(() => {
    const previous = editor.getAttributes("link").href || "";
    const href = window.prompt(
      "Where does this link go?\n\nA page on this site: /assessment/autism\nSomewhere else: https://…",
      previous
    );
    if (href === null) return;
    if (!href.trim()) return editor.chain().focus().unsetLink().run();
    editor.chain().focus().setLink({ href: href.trim() }).run();
  }, [editor]);

  const addCallout = useCallback(() => {
    const summary = window.prompt("The one-sentence summary at the top of the box:");
    if (!summary) return;
    const points = [];
    for (;;) {
      const point = window.prompt(`Takeaway ${points.length + 1} (leave empty to finish):`);
      if (!point) break;
      points.push(point);
    }
    editor.chain().focus().insertContent({ type: "keyTakeaways", attrs: { summary, points } }).run();
  }, [editor]);

  if (!editor) return <div className="editor">Loading the editor…</div>;

  const stats = readingStats(value || "");
  const on = (name, attrs) => (editor.isActive(name, attrs) ? "on" : "");

  return (
    <>
      <div className="toolbar" role="toolbar" aria-label="Formatting">
        <button type="button" className={on("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold">
          <strong>B</strong>
        </button>
        <button type="button" className={on("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic">
          <em>I</em>
        </button>
        <span className="sep" />
        <button type="button" className={on("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          H2
        </button>
        <button type="button" className={on("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          H3
        </button>
        <span className="sep" />
        <button type="button" className={on("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          • List
        </button>
        <button type="button" className={on("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          1. List
        </button>
        <button type="button" className={on("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          Quote
        </button>
        <span className="sep" />
        <button type="button" className={on("link")} onClick={addLink}>
          Link
        </button>
        <button type="button" onClick={() => setPickImage(true)}>
          Picture
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
        >
          Table
        </button>
        <button type="button" onClick={addCallout}>
          Key takeaways
        </button>
        <span className="sep" />
        <button
          type="button"
          disabled={!editor.isActive("image")}
          title="Full width / centred"
          onClick={() => {
            const align = editor.getAttributes("image")["data-align"] === "fullwidth" ? "center" : "fullwidth";
            editor.chain().focus().updateAttributes("image", { "data-align": align }).run();
          }}
        >
          Width
        </button>
        <button type="button" className="quiet" onClick={() => editor.chain().focus().undo().run()} title="Undo">
          Undo
        </button>
      </div>

      <div className="editor">
        <EditorContent editor={editor} />
      </div>

      <div className="spread" style={{ marginTop: "0.4rem" }}>
        <span className="hint">
          {stats.words.toLocaleString()} words · about {stats.minutes} minute{stats.minutes === 1 ? "" : "s"} to read
        </span>
        {allowJsonLd && <span className="hint">Structured data is kept as written.</span>}
      </div>

      {notes.length > 0 && (
        <Message level="warning">
          <strong>Tidied on the way in:</strong>
          <ul style={{ margin: "0.3rem 0 0", paddingLeft: "1.1rem" }}>
            {notes.map((note, i) => (
              <li key={i}>{note}</li>
            ))}
          </ul>
        </Message>
      )}

      {pickImage && (
        <div style={{ marginTop: "0.6rem" }}>
          <ImageField
            label="Add a picture to the body"
            value={null}
            onChange={(image) => {
              if (image?.url) {
                editor
                  .chain()
                  .focus()
                  .setImage({ src: image.url, alt: image.alt || "" })
                  .updateAttributes("image", { "data-align": "center" })
                  .run();
              }
              setPickImage(false);
            }}
          />
          <button type="button" className="quiet" onClick={() => setPickImage(false)}>
            Cancel
          </button>
        </div>
      )}
    </>
  );
}
